import './SearchModeControl.scss';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import DataTable, { IDataTableColumn } from '@srm/ui/data-table';
import { IExternalSearchDict, IForms } from '@models/Forms/IForms';
import { DictionariesService } from '@services/DictionariesService';
import { IField, IFieldElem, IFormData } from '@models/IFormData';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { convertListToMap, getFieldKeysByScheme } from '@utils/documentUtils';
import NestedFormBuilder from '@molecules/formbuilder/NestedFormBuilder/NestedFormBuilder';
import Button from '@srm/ui/button';
import { MdSearch } from 'react-icons/md';
import { sendModalMsg } from '@srm/ui/lib/modalMessages';
import { ValueType } from '@srm/ui/types/ValueType';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@srm/ui/controls/HookFormProvider';

export interface ISearchModeControlProp extends IExternalSearchDict {
    onSelectedRowItems: (value: any) => void;
}

/** Строка результата внешнего поиска: состав полей задаёт шаблон. */
type SearchRow = Record<string, unknown>;

const SearchModeControl: FC<ISearchModeControlProp> = (p: ISearchModeControlProp) => {
    const [form, setForm] = useState<IForms>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [searchResult, setSearchResult] = useState<SearchRow[]>([]);
    const methods = useForm<IFormData>({
        mode: 'onBlur', // "onChange"
    });
    const [fieldsData, setFieldsData] = useState<Record<string, IFieldElem>>();
    const fieldsName: number[] = [];
    useEffect(() => {
        DictionariesService.fetchExternalSearchScheme(p.searchForm).then((response) => {
            const keys = getFieldKeysByScheme(response.data.form.view.rows.row, false, response.data.form.changeActions);
            const uniqueKeys = keys.filter((v, i, a) => v !== undefined && a.indexOf(v) === i);
            const formFields: IField[] = [];
            uniqueKeys.forEach((key) => {
                formFields.push({ name: key, value: null });
            });
            const data = { fields: formFields };

            methods.reset(data);
            setFieldsData(convertListToMap(data.fields, ''));
            setForm(response.data.form);
        });
    }, [p.searchForm]);

    /** Колонки результата: имена и порядок задаёт шаблон внешнего поиска. */
    const columns = useMemo<IDataTableColumn<SearchRow>[]>(
        () =>
            (p.gridAttribute?.attrs ?? []).map((attr) => ({
                field: attr.key,
                header: attr.name,
                dataType: ValueType.Text,
                width: attr.width || undefined,
                widthHint: !attr.width,
            })),
        [p.gridAttribute?.attrs],
    );

    // Выбор одиночный: наружу уходит строка или её отсутствие
    const onSelectionChange = useCallback(
        (_keys: string[], rows: SearchRow[]) => {
            p.onSelectedRowItems(rows.length > 0 ? rows[0] : undefined);
        },
        [p],
    );

    const gridControl = (
        <DataTable<SearchRow>
            columns={columns}
            data={searchResult}
            getRowId={(row) => String(row[p.resultKey] ?? '')}
            selectable
            singleSelection
            selectOnRowClick
            onSelectionChange={onSelectionChange}
            paging={false}
            emptyText="Нет строк"
            overflowTooltip
        />
    );

    const onSubmit: SubmitHandler<IFormData> = (data: IFormData) => {
        const formData = {} as Record<string, any>;

        for (const ele of data.fields) {
            formData[ele.name.substring(1)] = ele.value;
        }
        setIsLoading(true);
        DictionariesService.fetchExternalSearch(p.searchApiUrl, formData)
            .then((response) => {
                setSearchResult(response.data ?? []);
            })
            .catch((reason) => {
                sendModalMsg({
                    message: [reason[0]],
                    type: 'error',
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return form ? (
        <div className="dict-container-search">
            <div className="dict-container-search__form-wrapper">
                <FormProviderControl changeActions={form?.changeActions} formMethods={methods} className="doc-form dict-form" onSubmit={onSubmit}>
                    <HookFormProvider controller={Controller}>
                        <div className="dict-container-search-form">
                            <NestedFormBuilder
                                fields={fieldsData!}
                                setError={(errors?: string[]) => {
                                    if (errors) {
                                        sendModalMsg({
                                            message: errors,
                                            type: 'error',
                                        });
                                    }
                                }}
                                rows={form?.view}
                                isEdit={false}
                                isNew={true}
                                formMethods={methods}
                                includedFields={fieldsName}
                                uniqueKey={''}
                            />
                        </div>
                        <div className="dict-search-buttons">
                            <Button
                                variant="primary"
                                size="sm"
                                type="submit"
                                aria-label="Найти"
                                disabled={isLoading}
                                loading={isLoading}
                                startAdornment={<MdSearch />}
                            >
                                Найти
                            </Button>
                        </div>
                    </HookFormProvider>
                </FormProviderControl>
            </div>
            <div className="dict-container-search__grid">{gridControl}</div>
        </div>
    ) : (
        <></>
    );
};

export default SearchModeControl;
